import { FC, ReactElement } from 'react';

import classNames from 'classnames';

import { Customer } from '../../entities/@account/Customer/Customer';
import { UspItem } from '../../entities/@blocks/UspSection/UspSection';
import { CheckoutDetailsFormData } from '../../entities/@forms/Checkout/Checkout';
import { CouponCodeFormData } from '../../entities/@forms/CouponCodeForm/CouponCodeForm';
import { PaymentStepFormData } from '../../entities/@forms/PaymentForm/PaymentForm';
import { CheckoutTab } from '../../entities/@shop/Checkout/Checkout';
import { ExtendedOrderInterface } from '../../entities/@shop/Order/Order';
import { PaymentMethod } from '../../entities/@shop/PaymentMethod/PaymentMethod';
import { Address } from '../../entities/Address/Address';
import { ProductQuantitySubmitFormData } from '../@forms/CartItemForm/validations';
import {
    CartSection,
    DetailsStep,
    PaymentStep,
    StartStep,
} from './subcomponents';

import './Checkout.scss';

interface CheckoutProps extends ExtendedOrderInterface {
    isInitiallyLoading: boolean;
    isLoading: boolean;
    disablePayButton?: boolean;
    tab?: CheckoutTab;
    customer?: Customer;
    addresses: Address[];
    paymentMethods?: PaymentMethod[];
    uspItems: UspItem[];
    onAddCouponCode: (data: CouponCodeFormData) => void;
    onRemoveCouponCode: () => void;
    onGoToCheckout: () => void;
    onSubmitDetails: (formData: CheckoutDetailsFormData) => void;
    setPaymentData: (data: PaymentStepFormData) => void;
    onSubmitPayment: () => void;
    onUpdateCartItem: (formData: ProductQuantitySubmitFormData) => void;
    onRemoveItemFromCart: (cartItemId: string) => void;
    onClearCart: () => void;
    className?: string;
}

export const Checkout: FC<CheckoutProps> = ({
    isInitiallyLoading,
    isLoading,
    disablePayButton,
    tab,
    customer,
    addresses,
    paymentMethods,
    uspItems,
    id,
    items,
    subTotalPrice,
    handlingPrice,
    totalPrice,
    discounts,
    onAddCouponCode,
    onRemoveCouponCode,
    onGoToCheckout,
    onSubmitDetails,
    setPaymentData,
    onUpdateCartItem,
    onRemoveItemFromCart,
    onClearCart,
    onSubmitPayment,
    className = '',
}): ReactElement => {
    const activeCheckout = tab !== CheckoutTab.order;
    const isPaymentStep = tab === CheckoutTab.payment;

    const checkoutClassNames = classNames('checkout', {
        'checkout-layout': activeCheckout,
    }, className);

    const wrapperClassNames = classNames('checkout__wrapper', {
        'checkout__wrapper--has-grid-layout': activeCheckout,
    });

    return (
        <div className={checkoutClassNames}>
            <div className={wrapperClassNames}>
                {tab === CheckoutTab.order && (
                    <StartStep
                        isInitiallyLoading={isInitiallyLoading}
                        isLoading={isLoading}
                        id={id}
                        paymentMethods={paymentMethods}
                        uspItems={uspItems}
                        items={items}
                        subTotalPrice={subTotalPrice}
                        handlingPrice={handlingPrice}
                        totalPrice={totalPrice}
                        discounts={discounts}
                        onGoToCheckout={onGoToCheckout}
                        onRemoveItemFromCart={onRemoveItemFromCart}
                        onUpdateCartItem={onUpdateCartItem}
                        onClearCart={onClearCart}
                        onSetCouponCode={onAddCouponCode}
                        onRemoveCouponCode={onRemoveCouponCode}
                    />
                )}

                {tab === CheckoutTab.details && (
                    <DetailsStep
                        customer={customer}
                        onSubmit={onSubmitDetails}
                    />
                )}

                {tab === CheckoutTab.payment && (
                    <PaymentStep
                        isLoading={isLoading}
                        addresses={addresses}
                        paymentMethods={paymentMethods}
                        setPaymentData={setPaymentData}
                    />
                )}

                {activeCheckout && (
                    <CartSection
                        isLoading={isLoading}
                        showPayButton={isPaymentStep}
                        disablePayButton={disablePayButton}
                        id={id}
                        paymentMethods={paymentMethods}
                        items={items}
                        subTotalPrice={subTotalPrice}
                        handlingPrice={handlingPrice}
                        totalPrice={totalPrice}
                        discounts={discounts}
                        onSetCouponCode={onAddCouponCode}
                        onRemoveCouponCode={onRemoveCouponCode}
                        onSubmitPayment={onSubmitPayment}
                    />
                )}
            </div>
        </div>
    );
};
